import React, { useState, useContext } from 'react';
import clsx from 'clsx';
import {
  Icon,
  Icons,
  InputAdornment,
  Typography,
  styled,
} from '@nextiva/next-ui-components';
import { useSelector } from 'react-redux';
import Fuse from 'fuse.js';
import { string, number, arrayOf, oneOfType, shape } from 'prop-types';
import { SaveUserChangesModal } from 'containers/pages/ManageUsers/EditUser/Sections/LicenseSection/PurchaseAdditionalLicenses/SaveUserChangesModal';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  StyledSearchTextField,
  StyledSearchIcon,
  StyledClearIcon,
} from 'components/SearchBar';

import { Item } from './Item';

const fuseOptions = {
  shouldSort: false,
  threshold: 0.25,
  keys: ['text'],
};

const BuyMoreLicensesLinkContainer = styled('div')`
  display: flex;
  justify-content: space-between;
`;
const StyledShoppingCartIcon = styled(Icon)`
  transform: scale(0.88);
`;
const StyledLicensesText = styled(Typography)`
  margin: 0px 4px 0px 8px;
  color: ${({ theme }) => theme.palette.blueGrey.A600};
`;
const StyledLicensingLink = styled('a')`
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
`;

const LICENSES_LINK = 'apps/platform-admin/#/licensing/licenses';

export const MultiSelectSearch = ({
  title,
  placeholder = '',
  options,
  selectedItems,
  onClick,
  tooltipMessage,
  unsavedUserChanges,
  saveChanges,
  enablePurchaseLicenseLink,
  'data-testid': dataTestId,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [saveChangesModalOpen, setSaveChangesModalOpen] = useState(false);

  const purchaseLicenseFlowGrantStatus = useSelector(
    (state) =>
      state.globalPolicyDecisionRequestState.purchaseLicenseFlowGrantStatus,
  );
  const accountV2Summary = useSelector(
    (state) => state.globalAccountV2SummaryState.globalAccountV2Summary,
  );
  const inAppPurchaseEnabled =
    accountV2Summary?.attributes?.inAppPurchaseEnabled === 'true' ||
    accountV2Summary?.attributes?.inAppPurchaseEnabled === true;
  const freemiumFlowEnabled =
    accountV2Summary?.attributes?.freemium === 'true' ||
    accountV2Summary?.attributes?.freemium === true;

  const {
    featureState: { purchaseLicenseFlow },
  } = useContext(FeatureContext);

  const isPurchaseLicenseFlowEnabled =
    purchaseLicenseFlow &&
    inAppPurchaseEnabled &&
    purchaseLicenseFlowGrantStatus &&
    !freemiumFlowEnabled;

  const fuse = new Fuse(options, fuseOptions);

  const displayOptions = searchValue ? fuse.search(searchValue) : options;

  const onSearchChange = (e) => {
    setSearchValue(e.target.value.trim());
  };

  const clearHandler = () => setSearchValue('');

  const onGoToLicensingClick = () => {
    if (unsavedUserChanges) {
      setSaveChangesModalOpen(true);
    } else {
      window.location = `${window.location.origin}/${LICENSES_LINK}`;
    }
  };

  const onSaveButtonClick = () => {
    setSaveChangesModalOpen(false);
    saveChanges(true);
  };

  const onCancelButtonClick = () => {
    setSaveChangesModalOpen(false);
  };

  return (
    <>
      {saveChangesModalOpen && (
        <SaveUserChangesModal
          onSaveButtonClick={onSaveButtonClick}
          onCancelButtonClick={onCancelButtonClick}
        />
      )}
      <div
        className="m-multiselect-search"
        data-testid="PlatformAdminUI-UsersLink-MultiSelectSearch"
      >
        <div
          className={clsx('m-multiselect-search__title', 'Hint')}
          data-testid="PlatformAdminUI-UsersLink-MultiSelectSearch-Title"
        >
          {isPurchaseLicenseFlowEnabled && enablePurchaseLicenseLink ? (
            <BuyMoreLicensesLinkContainer>
              {title}
              <div>
                <StyledShoppingCartIcon
                  icon={Icons.shoppingCart}
                  aria-label="Purchase Licenses"
                  data-testid="PlatformAdminUI-ManageUserDrawer-LicenseSelect-ShoppingCartIcon"
                />
                <StyledLicensesText variant="caption2">
                  Need more licenses?
                </StyledLicensesText>
                <StyledLicensingLink
                  onClick={onGoToLicensingClick}
                  role="link"
                  data-testid="PlatformAdminUI-ManageUserDrawer-LicenseSelect-LicensingLink"
                >
                  Go to licensing
                </StyledLicensingLink>
              </div>
            </BuyMoreLicensesLinkContainer>
          ) : (
            title
          )}
        </div>
        <div
          className="m-multiselect-search__search"
          data-testid="PlatformAdminUI-UsersLink-MultiSelectSearch-Search"
        >
          <StyledSearchTextField
            hiddenLabel
            id="search"
            value={searchValue}
            fullWidth
            placeholder={placeholder}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StyledSearchIcon icon={Icons.search} />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <StyledClearIcon
                    icon={Icons.close}
                    onClick={clearHandler}
                    data-testid={dataTestId}
                  />
                </InputAdornment>
              ),
            }}
            data-testid={dataTestId}
          />
        </div>
        <div
          className="m-multiselect-search__list"
          data-testid="PlatformAdminUI-UsersLink-MultiSelectSearch-List"
        >
          {displayOptions.map((item) => {
            const isSelected = selectedItems.includes(item.id);

            return (
              <Item
                item={item}
                key={item.id}
                id={item.id}
                selected={isSelected}
                text={item.text}
                onClick={onClick}
                disabled={item.disabled}
                tooltipMessage={tooltipMessage}
                data-testid={dataTestId}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

MultiSelectSearch.propTypes = {
  options: arrayOf(
    shape({
      id: oneOfType([string, number]),
      text: string,
    }),
  ).isRequired,
  selectedItems: arrayOf(oneOfType([string, number])).isRequired,
  placeholder: string,
  title: string,
};

MultiSelectSearch.defaultProps = {
  title: '',
  placeholder: '',
};
